import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import {
  ArrowRight, Zap, ShieldCheck, Crown, Sparkles,
  Wrench, Star, CheckCircle2, DollarSign, Target,
  UserPlus, ListChecks, Briefcase, CreditCard,
} from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import FixyBeeMark from "@/components/brand/FixyBeeMark";
import { useSmartCTA } from "@/hooks/useSmartCTA";
import { readReturnTo } from "@/lib/returnTo";
import api from "@/lib/api";

const STEPS = [
  { n: "01", Icon: UserPlus, key: "s1" },
  { n: "02", Icon: ListChecks, key: "s2" },
  { n: "03", Icon: ShieldCheck, key: "s3" },
  { n: "04", Icon: Briefcase, key: "s4" },
];

const SOCIAL_STATS = [
  { stat: "2.4×", key: "stat1" },
  { stat: "100%", key: "stat2" },
  { stat: "60s", key: "stat3" },
];

export default function BecomeFixyBee() {
  const cta = useSmartCTA();
  // This page sits between a CTA and /signup — "Cotizar este trabajo" on a
  // public job lands here first — so it has to hand on the destination it was
  // given instead of ending the chain.
  const [searchParams] = useSearchParams();
  const returnTo = readReturnTo(searchParams);
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    api.get("/billing/config").then((r) => setPlans(r.data?.plans || [])).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen flex flex-col bg-cream" data-testid="become-fixybee-page">
      <Navbar />

      {/* HERO */}
      <section className="section bg-honeycomb pb-12" data-testid="bff-hero">
        <div className="container-app grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div className="stagger">
            <span className="badge mb-6" data-testid="bff-hero-badge">
              <Crown size={12} className="text-hive-ink" /> {t("becomeFixybee.kicker")}
            </span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] text-balance text-worker">
              {t("becomeFixybee.title1")} <br />
              <span className="text-hive-ink">{t("becomeFixybee.title2")}</span>
            </h1>
            <p className="mt-6 text-lg text-smoke max-w-xl leading-relaxed">
              {t("becomeFixybee.subhead")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="bff-hero-ctas">
              <Link to={cta.isProvider ? cta.becomeFixyBee() : cta.signup("provider", { returnTo })} className="fx-btn fx-btn--primary text-base" data-testid="bff-hero-signup">
                {cta.isProvider ? t("becomeFixybee.ctaDashboard") : t("becomeFixybee.ctaSignup")} <ArrowRight size={18} />
              </Link>
              <Link to="/pricing" className="fx-btn fx-btn--secondary text-base" data-testid="bff-hero-pricing">
                {t("becomeFixybee.ctaPricing")}
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-smoke">
              <span className="inline-flex items-center gap-2"><CheckCircle2 size={16} className="text-success" /> {t("common.free")}</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 size={16} className="text-success" /> {t("landing.trust.verified")}</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 size={16} className="text-success" /> {t("landing.trust.safe")}</span>
            </div>
          </div>

          <div className="relative hidden lg:flex items-center justify-center">
            <div className="absolute inset-0 bg-hive/10 rounded-lg blur-3xl"></div>
            <div className="relative animate-bee-wings">
              <FixyBeeMark size={300} />
            </div>
          </div>
          <div className="lg:hidden flex justify-center"><FixyBeeMark size={160} /></div>
        </div>
      </section>

      {/* SOCIAL STATS */}
      <section className="section pt-12 pb-8" data-testid="bff-stats">
        <div className="container-app">
          <div className="grid md:grid-cols-3 gap-4">
            {SOCIAL_STATS.map((s) => (
              <div key={s.key} className="card-base p-6 text-center" data-testid={`stat-${s.stat}`}>
                <p className="font-mono text-5xl font-extrabold text-hive-ink">{s.stat}</p>
                <p className="mt-2 text-smoke leading-relaxed">{t(`becomeFixybee.stats.${s.key}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="section pt-12" id="benefits" data-testid="bff-benefits">
        <div className="container-app">
          <div className="text-center mb-12">
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold text-worker">
              {t("becomeFixybee.valueProp")}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { Icon: Target, key: "vp1" },
              { Icon: Sparkles, key: "vp2" },
              { Icon: CreditCard, key: "vp3" },
              { Icon: Crown, key: "vp4" },
            ].map((b) => {
              const Icon = b.Icon;
              return (
                <div key={b.key} className="card-base p-6" data-testid={`benefit-${b.key}`}>
                  <Icon size={28} className="text-hive-ink" />
                  <h3 className="mt-3 font-display text-xl font-bold text-worker">{t(`becomeFixybee.${b.key}Title`)}</h3>
                  <p className="mt-2 text-sm text-smoke leading-relaxed">{t(`becomeFixybee.${b.key}Body`)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW TO GET STARTED */}
      <section className="section bg-linen" id="how-to-start" data-testid="bff-steps">
        <div className="container-app">
          <div className="text-center mb-12">
            <span className="badge bg-worker text-hive">{t("becomeFixybee.steps2.kicker")}</span>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold text-worker">
              {t("becomeFixybee.steps2.title")}
            </h2>
            <p className="mt-3 text-smoke max-w-xl mx-auto">
              {t("becomeFixybee.steps2.subtitle")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {STEPS.map((s) => {
              const Icon = s.Icon;
              return (
                <div key={s.n} className="card-base p-6 relative" data-testid={`step-${s.n}`}>
                  <span className="absolute top-4 right-4 font-mono text-sm text-hive-ink">{s.n}</span>
                  <Icon size={28} className="text-hive-ink" />
                  <h3 className="mt-3 font-display text-xl font-bold text-worker">{t(`becomeFixybee.steps2.${s.key}Title`)}</h3>
                  <p className="mt-2 text-sm text-smoke leading-relaxed">{t(`becomeFixybee.steps2.${s.key}Body`)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="section bg-worker text-cream" data-testid="bff-pricing-teaser">
        <div className="container-app grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="badge bg-hive text-worker mb-4">{t("becomeFixybee.teaser.kicker")}</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight">
              {t("becomeFixybee.teaser.title1")} <br />{t("becomeFixybee.teaser.title2")}
            </h2>
            <p className="mt-4 text-cream/80 text-lg leading-relaxed max-w-md">
              {t("becomeFixybee.teaser.body")}
            </p>
            <ul className="mt-6 space-y-3 text-cream/90">
              {/* PLANES: one line per plan from /billing/config — price and
                  quotes a month are platform data, never typed into copy
                  (these three lines once said "10" and "unlimited"). */}
              {plans.map((p) => (
                <li key={p.plan_id} className="flex items-start gap-2" data-testid={`bff-plan-${p.plan_id}`}>
                  <CheckCircle2 size={18} className="text-hive shrink-0 mt-0.5" />
                  <span>
                    <Trans
                      i18nKey="becomeFixybee.teaser.planLine"
                      count={Number(p.included_quotes_per_month) || 0}
                      values={{
                        name: p.name,
                        price: `$${Number(p.price_monthly_usd || 0).toFixed(0)}`,
                        count: Number(p.included_quotes_per_month) || 0,
                      }}
                      components={{ strong: <strong className="text-cream" /> }}
                    />
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex gap-3 flex-wrap">
              <Link to="/pricing" className="fx-btn fx-btn--secondary" data-surface="inverse" data-testid="bff-pricing-cta">
                {t("becomeFixybee.teaser.cta")} <ArrowRight size={18} />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              { Icon: DollarSign, key: "f1" },
              { Icon: Zap, key: "f2" },
              { Icon: ShieldCheck, key: "f3" },
              { Icon: Wrench, key: "f4" },
            ].map((f) => {
              const Icon = f.Icon;
              return (
                <div key={f.key} className="rounded-md bg-espresso border border-cream/10 p-5">
                  <Icon size={22} className="text-hive" />
                  <p className="mt-3 font-display font-bold text-cream">{t(`becomeFixybee.teaser.${f.key}Label`)}</p>
                  <p className="text-sm text-cream/70">{t(`becomeFixybee.teaser.${f.key}Body`)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section bg-hive" data-testid="bff-final-cta">
        <div className="container-app text-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-worker">
            {t("becomeFixybee.final.title")}
          </h2>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link to={cta.isProvider ? cta.becomeFixyBee() : cta.signup("provider", { returnTo })} className="fx-btn fx-btn--master text-base" data-testid="bff-final-signup">
              {cta.isProvider ? t("becomeFixybee.ctaDashboard") : t("becomeFixybee.final.ctaSignup")} <ArrowRight size={18} />
            </Link>
            <Link to="/how-it-works" className="fx-btn fx-btn--ghost text-worker hover:bg-worker/10" data-testid="bff-final-how">
              {t("becomeFixybee.final.ctaPricing")}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
